/**
 * deliveryWindow — the "4 PM to 4 PM" batch maths, in IST.
 *
 * Rocket07 delivers once each evening. Every order placed between one day's
 * cutoff and the next day's cutoff is ONE delivery batch. The cutoff is stored
 * per college as minutes past midnight IST (960 = 4:00 PM). Order timestamps
 * are UTC in the database, so everything here converts through IST carefully.
 *
 * A batch is named by its DELIVERY DATE (the day it goes out): the batch for
 * delivery date D covers [cutoff(D-1), cutoff(D)). At exactly the cutoff a new
 * batch begins, so the boundary is inclusive-start, exclusive-end.
 *
 * WHY THIS IS IN `shared` RATHER THAN THE SERVER
 * ----------------------------------------------
 * The checkout page tells the student which day their order arrives, and the
 * admin Excel export groups orders into the same batches. If those two ever
 * disagreed the customer would be promised a day the batch does not deliver
 * on — so both sides run this one implementation. It is pure date arithmetic
 * with no framework and no environment, which is exactly what belongs here.
 */

const IST_OFFSET_MS = 330 * 60 * 1000; // IST = UTC + 5:30
const DAY_MS = 24 * 60 * 60 * 1000;

/** IST wall-clock parts for a given instant. */
function istParts(ms) {
  const d = new Date(ms + IST_OFFSET_MS); // shift so getUTC* reads as IST
  return {
    year: d.getUTCFullYear(),
    month: d.getUTCMonth(), // 0-based
    day: d.getUTCDate(),
    minutesOfDay: d.getUTCHours() * 60 + d.getUTCMinutes(),
  };
}

/** "YYYY-MM-DD" (IST) for an instant. */
export function istDateString(ms = Date.now()) {
  const { year, month, day } = istParts(ms);
  return `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

/** The UTC instant of `cutoffMinutes` IST on the IST calendar date `dateStr`. */
function cutoffInstant(dateStr, cutoffMinutes) {
  const [y, m, d] = dateStr.split('-').map(Number);
  // Date.UTC(...,00:00) is that date at UTC midnight; shift back to IST midnight,
  // then add the cutoff minutes.
  return new Date(Date.UTC(y, m - 1, d, 0, 0, 0, 0) - IST_OFFSET_MS + cutoffMinutes * 60000);
}

/**
 * The batch window (UTC Dates) for a given delivery date.
 * @returns {{ start: Date, end: Date }}
 */
export function windowForDeliveryDate(deliveryDateStr, cutoffMinutes) {
  const end = cutoffInstant(deliveryDateStr, cutoffMinutes);
  const start = new Date(end.getTime() - DAY_MS); // same cutoff a day earlier
  return { start, end };
}

/**
 * The delivery date (YYYY-MM-DD, IST) of the batch currently being collected —
 * i.e. the next cutoff that hasn't passed yet. Before today's cutoff → today;
 * after it → tomorrow.
 */
export function currentDeliveryDate(cutoffMinutes, nowMs = Date.now()) {
  const { minutesOfDay } = istParts(nowMs);
  const todayMs = minutesOfDay < cutoffMinutes ? nowMs : nowMs + DAY_MS;
  return istDateString(todayMs);
}

/** minutes-past-midnight → "4:00 PM" (12-hour, for display). */
export function formatCutoff(minutes) {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  const period = h < 12 ? 'AM' : 'PM';
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:${String(m).padStart(2, '0')} ${period}`;
}

/** minutes → "2h 14m", "45m", "1m". For "order within ..." countdowns. */
export function formatDuration(minutes) {
  const safe = Math.max(0, Math.round(minutes));
  const h = Math.floor(safe / 60);
  const m = safe % 60;
  if (h === 0) return `${Math.max(1, m)}m`;
  if (m === 0) return `${h}h`;
  return `${h}h ${m}m`;
}

/**
 * Everything the checkout page needs to state the delivery day truthfully.
 *
 * The page used to hardcode "arrives Today, 7:00 PM", which is simply false
 * for any order placed after the cutoff — those go out with the NEXT evening's
 * batch. This derives the real answer from the college's configured cutoff.
 *
 * @param {number} cutoffMinutes  minutes past IST midnight (960 = 4:00 PM)
 * @param {number} [nowMs]        instant to judge from; injectable for tests
 * @returns {{
 *   date: string,            // "YYYY-MM-DD" (IST) the batch is delivered on
 *   isToday: boolean,
 *   dayLabel: string,        // "Today" | "Tomorrow"
 *   dateLabel: string,       // "5 Aug"
 *   cutoffLabel: string,     // "4:00 PM"
 *   minutesToCutoff: number, // time left to make THIS batch
 * }}
 */
export function describeDelivery(cutoffMinutes, nowMs = Date.now()) {
  const { minutesOfDay } = istParts(nowMs);
  const date = currentDeliveryDate(cutoffMinutes, nowMs);
  const isToday = date === istDateString(nowMs);

  /**
   * Before the cutoff, the batch closes today and delivers tonight. After it,
   * today's batch has already closed, so the order joins tomorrow's — which
   * does not close until tomorrow's cutoff, hence the wrap through midnight.
   */
  const minutesToCutoff = isToday
    ? cutoffMinutes - minutesOfDay
    : 1440 - minutesOfDay + cutoffMinutes;

  /**
   * Format the delivery DATE itself (midday avoids any edge-of-day surprise).
   * No weekday: the answer is only ever today or tomorrow, so `dayLabel`
   * already carries the useful part and "Today, Wed, 5 Aug" just reads badly.
   */
  const noonOfDate = cutoffInstant(date, 12 * 60);
  const dateLabel = new Intl.DateTimeFormat('en-IN', {
    day: 'numeric',
    month: 'short',
    timeZone: 'Asia/Kolkata',
  }).format(noonOfDate);

  return {
    date,
    isToday,
    dayLabel: isToday ? 'Today' : 'Tomorrow',
    dateLabel,
    cutoffLabel: formatCutoff(cutoffMinutes),
    minutesToCutoff,
  };
}

/** A human label for a batch window, e.g. "26 Jul 4:00 PM → 27 Jul 4:00 PM" (IST). */
export function formatWindowLabel(start, end) {
  const opts = { day: '2-digit', month: 'short', hour: 'numeric', minute: '2-digit', hour12: true };
  const fmt = (d) =>
    new Intl.DateTimeFormat('en-IN', { ...opts, timeZone: 'Asia/Kolkata' }).format(d);
  return `${fmt(start)} → ${fmt(end)}`;
}

/**
 * The delivery fee for an order, from the college's own settings. ONE formula,
 * used by the cart, the checkout display, AND the server that actually charges —
 * so the three can never disagree about what the student pays.
 *
 *   • flat `deliveryFee` on every order,
 *   • except FREE at/above `freeDeliveryThreshold` (0 = never free),
 *   • and always FREE on an empty cart or when the fee itself is 0.
 *
 * @param {{ deliveryFee?: number, freeDeliveryThreshold?: number }} college
 * @param {number} subtotal
 * @returns {number} the fee in rupees
 */
export function computeDeliveryFee(college, subtotal) {
  const fee = Number(college?.deliveryFee) || 0;
  const threshold = Number(college?.freeDeliveryThreshold) || 0;
  const amount = Number(subtotal) || 0;
  if (fee <= 0 || amount <= 0) return 0;
  if (threshold > 0 && amount >= threshold) return 0;
  return fee;
}
